import {
    AnyElementType,
    AnyElementValue,
    ComparisonItem,
    ComparisonItems,
    ComparisonReferences,
    CompositeNavigation,
    CompositeType,
    ConditionalType,
    DecorationType,
    DisplayType,
    EditorType,
    ElementsValues,
    ElementsValuesInfo,
    ElementsValuesType,
    FieldValidationError,
    getComparisonElement,
    getElementsValues,
    getSchemaElementDefaultValue,
    history,
    Nullable,
    RefElementsMap,
    SchemaElementTypes,
    StringType,
    UploadsConfig,
} from "@data-force/app";
import {match, matchPath} from "react-router";
import {getFormErrors} from "@data-force/app/utils/common";
import {UploadFile} from "antd/lib/upload/interface";
import HistoryParams from "../interfaces/historyParams";
import {
    CountValidTasksData,
    GetFormErrorsPayload,
    GetUpdatedElementMetadataInfo,
    IntakeCompletionPercentageData,
    JobExpiredInfo,
    SetJobMetadataDefaultsData,
    SetTaskMetadataDefaultsData,
    StatusData,
    StatusDataInfo,
    TaskStatusCount,
} from "../interfaces/store/actions/payload/job";
import {
    Status,
    StatusIconData,
    StatusIconInfo,
    StatusStats,
    Task,
    TaskEntity,
    TaskPhase,
} from "../interfaces/store/actions/payload/task";
import {BucketCount, SelectedFiles, UploadsBucket} from "../interfaces/store/reducers/uploadFilesState";
import {
    BucketCountData,
    BucketKeyData,
    DetermineUploadedFilesData,
    InitUploadsData,
    TaskIdBucketKey,
    UploadsData,
    UploadsFileNamesData,
    UploadsHolder,
    UploadsHolderData,
    ValidByBucketsData,
} from "../interfaces/store/actions/payload/fileValidation";
import {PhaseAccessStatus} from "../enums/enums";

export const getHistoryParams = (): HistoryParams => {
    const matchResult: Nullable<match<HistoryParams>> = matchPath(
        history.location.pathname, "/:idpId/:phase/:projectId/:jobId/:jobPhaseId/:taskId?/:taskEntityId?",
    );
    return matchResult?.params!;
};

/**
 * Regex for searching placeholders in string template
 */
const formatStringRegex: RegExp = /{(\d+)}/g;

/**
 * Format string with values and template
 * @param {string} template
 * @param {string[]} values
 * @return {string}
 */
export const formatString = (template: string, ...values: string[]): string => (
    template.replace(formatStringRegex, (placeholderMatch: string, index: number): string => (
        typeof values[index] !== "undefined" ? values[index] : placeholderMatch))
);

export const calculateStatusCount = (statusStats: Nullable<StatusStats>): TaskStatusCount => ({
    ready: Math.abs(statusStats?.READY || 0),
    inProgress: Math.abs(statusStats?.IN_PROGRESS || 0),
    completed: Math.abs(statusStats?.COMPLETED || 0),
    notStarted: Math.abs(statusStats?.NOT_STARTED || 0),
    skipped: Math.abs(statusStats?.SKIPPED || 0),
    failed: Math.abs(statusStats?.FAILED || 0),
    reopened: Math.abs(statusStats?.REOPENED || 0),
    rejected: Math.abs(statusStats?.REJECTED || 0),
});

export const calculateNumberOfTasks = (statusStats: Nullable<StatusStats>): number => {
    let numberOfTasks: number = 0;
    if (statusStats) {
        const taskStatusCount: TaskStatusCount = calculateStatusCount(statusStats);
        const {ready, reopened, inProgress}: TaskStatusCount = taskStatusCount;
        numberOfTasks = ready + reopened + inProgress;
    }
    return numberOfTasks;
};

/**
 * Returns true if job level (batchFieldReferences) metadata is valid.
 *
 * @param {GetFormErrorsPayload} getFormErrorsPayload
 */
const isJobMetadataValid = (getFormErrorsPayload: GetFormErrorsPayload): boolean => {
    const {
        jobId, schemaKey, elementsValuesMap, editor, translate,
    }: GetFormErrorsPayload = getFormErrorsPayload;
    const jobMetadata: ElementsValues = getElementsValues({
        metadataId: jobId,
        elementsValuesType: ElementsValuesType.JOB,
        schemaKey,
        elementsValuesMap,
    });
    const validationErrors: FieldValidationError[] = getFormErrors(
        jobMetadata,
        editor,
        editor?.batchFieldReferences || [],
        schemaKey,
        translate,
    );
    return !validationErrors.length;
};

/**
 * Returns array of metadata validation errors, for given {TaskEntity} ID.
 *
 * @param {GetFormErrorsPayload} getFormErrorsPayload
 * @param {string} taskId
 */
const getTaskValidationErrors = (
    getFormErrorsPayload: GetFormErrorsPayload,
    taskId: string,
): FieldValidationError[] => {
    const {
        schemaKey, elementsValuesMap, editor, translate,
    }: GetFormErrorsPayload = getFormErrorsPayload;
    const taskMetadata: ElementsValues = getElementsValues({
        metadataId: taskId,
        elementsValuesType: ElementsValuesType.TASK,
        schemaKey,
        elementsValuesMap,
    });
    return getFormErrors(
        taskMetadata,
        editor,
        editor?.spanReferences || [],
        schemaKey,
        translate,
    );
};

/**
 * Determines the list of tasks handled in current intaker session.
 *
 * @param {TaskEntity[]} tasks
 * @return {TaskEntity[]}
 */
export const getSessionTasks = (tasks: TaskEntity[]): TaskEntity[] => {
    const operationalTasks: TaskEntity[] = tasks.filter(
        (task: TaskEntity): boolean => [Status.REOPENED, Status.IN_PROGRESS, Status.READY].includes(task.status),
    );
    return (operationalTasks.length ? operationalTasks : tasks);
};

/**
 * Returns boolean true, if all project metadata is valid.
 * Takes in consideration job level metadata, and metadata of each task.
 *
 * @param {GetFormErrorsPayload} getFormErrorsPayload
 */
export const isMetadataValid = (getFormErrorsPayload: GetFormErrorsPayload): boolean => {
    const {tasks}: GetFormErrorsPayload = getFormErrorsPayload;

    let metadataValid: boolean = isJobMetadataValid(getFormErrorsPayload);
    if (metadataValid) {
        const sessionTasks: TaskEntity[] = getSessionTasks(tasks);

        const validationErrors: FieldValidationError[] = [];
        sessionTasks.forEach(({id: taskId}: TaskEntity) => {
            validationErrors.push(...getTaskValidationErrors(getFormErrorsPayload, taskId));
        });

        metadataValid = !validationErrors.length;
    }

    return metadataValid;
};

/**
 * Returns boolean true if metadata of given {Task} is valid.
 *
 * @param {GetFormErrorsPayload} getFormErrorsPayload
 * @param {Nullable<Task>} activeTask
 */
export const isTaskMetadataValid = (
    getFormErrorsPayload: GetFormErrorsPayload,
    activeTask: Nullable<Task>,
): boolean => {
    const {tasks}: GetFormErrorsPayload = getFormErrorsPayload;
    let metadataValid: boolean = isJobMetadataValid(getFormErrorsPayload);
    if (metadataValid) {
        const taskEntity: Nullable<TaskEntity> = tasks.find(
            (task: TaskEntity): boolean => task.payload === activeTask?.id,
        );
        if (taskEntity) {
            const errors: FieldValidationError[] = getTaskValidationErrors(getFormErrorsPayload, taskEntity.id);
            metadataValid = !errors.length;
        }
    }

    return metadataValid;
};

/**
 * Calculates Job css class, depending on status of Job tasks
 *
 * @return {string}
 */
export const calculateStatusData = (statusDataInfo: StatusDataInfo): StatusData => {
    const {
        statusStats, rejectedTaskCount, translate, translationPath, numberOfTasks,
    }: StatusDataInfo = statusDataInfo;
    let statusClass: string = "df-job-not-ready";
    let statusName: string = translate(`${translationPath}.${Status.NOT_STARTED}.text`) as string;
    if (statusStats) {
        const {
            notStarted, completed, reopened, rejected,
        }: TaskStatusCount = calculateStatusCount(statusStats);

        if (notStarted > 0 || numberOfTasks === 0) {
            statusClass = "df-job-not-ready";
        } else if ((rejectedTaskCount > 0 && rejectedTaskCount === numberOfTasks)
        || (completed > 0 && completed === numberOfTasks && rejectedTaskCount > 0)) {
            /*
            NOTE: "rejectedTaskCount" counts in the tasks that were rejected in the other phases. At the same time, those tasks will end up having the status of COMPLETED.
            In other words, certain number of tasks in "rejectedTaskCount" is also or counted in "completed".
            However, "rejectedTaskCount" ALSO counts in the tasks that were rejected within the Collection phase itself. Those tasks will have Status.REJECTED.

            Side note: the tasks that were selected to be reopened (in the QA phase, e.g.) ought to have status REOPENED before we open the Collection phase.
            However, once we open the Intaker, the status of the first of those tasks (and each following task, once selected) will have the status of IN_PROGRESS.
            Also, 'reopened' task status can come from the Collection phase.
            */
            statusClass = "df-job-rejected";
            statusName = translate(`${translationPath}.${Status.REJECTED}.text`) as string;
        } else if (completed > 0 && completed + rejected === numberOfTasks && rejectedTaskCount === 0) {
            statusClass = "df-job-finished";
            statusName = translate(`${translationPath}.${Status.COMPLETED}.text`) as string;
        } else if (reopened > 0) {
            statusClass = "df-job-reopened";
            statusName = translate(`${translationPath}.${Status.REOPENED}.text`) as string;
        } else {
            statusClass = "df-job-ready";
            statusName = translate(`${translationPath}.${Status.IN_PROGRESS}.text`) as string;
        }
    }

    return {statusClass, statusName};
};

export const getNavigationElements = (
    navigationElements: AnyElementType[],
    fieldRefs: string[],
    refEls: RefElementsMap,
    navigationType: CompositeNavigation,
    metadataInfo: ElementsValuesInfo,
): AnyElementType[] => {
    let updatedNavigationElements: AnyElementType[] = [...navigationElements];

    fieldRefs?.forEach((fieldRef: string) => {
        const currentElement: AnyElementType = refEls[fieldRef];

        if (navigationType === CompositeNavigation.GLOBAL) {
            const {schemaType}: AnyElementType = currentElement;
            if (schemaType === SchemaElementTypes.COMPOSITE_FIELD_TYPE) {
                const {navigation}: CompositeType = (currentElement as CompositeType);
                if (navigation === navigationType) {
                    updatedNavigationElements.push(currentElement);
                }
            } else if (schemaType === SchemaElementTypes.CONDITIONAL_TYPE) {
                const {
                    fieldReferences, refElements, whenReference, anyOf,
                }: ConditionalType = (currentElement as ConditionalType);

                if (fieldReferences && refElements) {
                    let skipElement: boolean = false;
                    if (whenReference) {
                        const {hidden, readOnly} = refElements[whenReference] as StringType;
                        if (hidden && readOnly) {
                            const jobMetadata: ElementsValues = getElementsValues(metadataInfo);
                            // If element is wrapped by conditional field which is in read only mode and hidden,
                            // and element does not meet the conditional value,
                            // that field should not be processed for navigation purposes
                            if (!anyOf.includes(jobMetadata[whenReference]?.toString() || "")) {
                                skipElement = true;
                            }
                        }
                    }

                    if (!skipElement) {
                        updatedNavigationElements = getNavigationElements(
                            updatedNavigationElements,
                            fieldReferences,
                            refElements,
                            navigationType,
                            metadataInfo,
                        );
                    }
                }
            }
        } else {
            // TODO implement specific behaviour for the rest of navigation types if needed
        }
    });

    return updatedNavigationElements;
};

export const getMediaUrls = (
    elementUrls: string[],
    fieldRefs: string[],
    refEls: RefElementsMap,
): string[] => {
    let updatedElementUrls: string[] = [...elementUrls];

    fieldRefs?.forEach((fieldRef: string) => {
        const currentElement: AnyElementType = refEls[fieldRef];
        const {descriptionUrl, schemaType}: AnyElementType = currentElement;

        if (descriptionUrl) {
            updatedElementUrls.push(descriptionUrl);
        }

        switch (schemaType) {
            case SchemaElementTypes.COMPOSITE_FIELD_TYPE: {
                const {fieldReferences, refElements}: CompositeType = currentElement as CompositeType;
                updatedElementUrls = getMediaUrls(updatedElementUrls, fieldReferences, refElements!);
                break;
            }
            case SchemaElementTypes.CONDITIONAL_TYPE: {
                const {fieldReferences, refElements}: ConditionalType = currentElement as ConditionalType;
                updatedElementUrls = getMediaUrls(updatedElementUrls, fieldReferences, refElements!);
                break;
            }
            case SchemaElementTypes.DECORATION_TYPE: {
                const {fieldReferences, refElements}: DecorationType = currentElement as DecorationType;
                updatedElementUrls = getMediaUrls(updatedElementUrls, fieldReferences, refElements!);
                break;
            }
            default:
                break;
        }
    });

    return updatedElementUrls;
};

/**
 * Flatten a multidimensional object.
 *
 * For given { a: 1, b: { c: 2 } }
 * Returns: { a: 1, c: 2}
 */
export const flattenObject = (obj: any): any => {
    const flattened: any = {};

    Object.keys(obj).forEach((key: string) => {
        if (obj[key] && typeof obj[key] === "object") {
            Object.assign(flattened, flattenObject(obj[key]));
        } else {
            flattened[key] = obj[key];
        }
    });

    return flattened;
};

/**
 * TaskContainer is valid if there is no rejection reasons
 *
 * @param {EditorType} editor
 * @param {ElementsValues} formData
 */
export const isTaskValid = (editor: Nullable<EditorType>, formData: ElementsValues): boolean => {
    let valid: boolean = true;
    if (formData && editor) {
        const {rejectionReasonReference}: EditorType = editor;
        if (rejectionReasonReference) {
            valid = !formData[rejectionReasonReference];
        }
    }
    return valid;
};

/**
 * Calculates job progress percentage
 *
 * @param {IntakeCompletionPercentageData} data
 */
export const calculateIntakeCompletionPercentage = (data: IntakeCompletionPercentageData): number => {
    const {
        totalTasks, rejected, completed, validTaskCount,
    }: IntakeCompletionPercentageData = data;

    return (totalTasks > 0
        ? Math.round(((completed + rejected + validTaskCount) / totalTasks) * 100)
        : 0
    );
};

/**
 * Merge upload buckets in to single object
 *
 * @param {SelectedFiles} selectedFiles
 * @return {UploadsBucket}
 */
export const mergeUploadBuckets = (selectedFiles: SelectedFiles): UploadsBucket => Object.values(selectedFiles)
    .reduce(((accumulator: UploadsBucket, bucket: UploadsBucket) => Object.assign(accumulator, bucket)), {});

/**
 * Creates array of upload files, from FileListState "bucket" structure.
 *
 * @param {SelectedFiles} selectedFiles
 * @return {UploadFile[]}
 */
export const getUploadFileList = (selectedFiles: SelectedFiles): UploadFile[] => {
    const uploadBucket: UploadsBucket = mergeUploadBuckets(selectedFiles);
    return Object.values(uploadBucket);
};

/**
 * Calculate number of uploaded files, for tasks in terminal status.
 *
 * @param {BucketCount} bucketCount
 * @return {number}
 */
export const getFinishedTasksCount = (bucketCount: BucketCount): number => Object.values(bucketCount)
    .reduce((accumulator: number, value: number): number => accumulator + value, 0);

/**
 * Calculates actual value of given reference.
 * Used for minCountReference and maxCountReference, in batchUploads (or taskUploads) schema configuration.
 *
 * @param {Nullable<string>} reference
 * @param {RefElementsMap} collectionRefElements
 * @param {ElementsValues} elementsValues
 * @return {Nullable<number>}
 */
export const calculateCountReference = (
    reference: Nullable<string>,
    collectionRefElements: RefElementsMap,
    elementsValues: ElementsValues,
): Nullable<number> => {
    let count: Nullable<number> = null;
    if (reference) {
        const minCountElement: Nullable<AnyElementType> = collectionRefElements[reference];
        if (minCountElement) {
            const {value}: ComparisonItem = getComparisonElement(minCountElement, elementsValues);
            if (value && Number.isInteger(value)) {
                count = Number(value);
            }
        }
    }
    return count;
};
/**
 * Retrieves file names of uploaded task files
 * @param {UploadsFileNamesData} uploadsData
 * @return {string[]}
 */
export const getUploadsFileNames = (uploadsData: UploadsFileNamesData): string[] => {
    const {
        tasks, schemaKey, elementsValuesMap, fileNameReference, uploadBucket,
    }: UploadsFileNamesData = uploadsData;
    const uploadsFileNames: string[] = [];

    if (fileNameReference) {
        tasks.filter((task: TaskEntity): boolean => task.status !== Status.IN_PROGRESS)
            .forEach((task: TaskEntity): void => {
                const taskElementsValues: ElementsValues = getElementsValues({
                    metadataId: task.id,
                    elementsValuesType: ElementsValuesType.TASK,
                    schemaKey,
                    elementsValuesMap,
                });

                if (taskElementsValues[fileNameReference] && !uploadBucket[task.payload]) {
                    uploadsFileNames.push(taskElementsValues[fileNameReference] as string);
                }
            });
    }

    return uploadsFileNames;
};

/**
 * Creates UploadsHolder for given uploadsConfig (schema object).
 * Initializes name, minCount and maxCount properties.
 *
 * @param {UploadsHolderData} uploadsHolderData
 * @return {UploadsHolder}
 */
export const initUploadsHolder = (uploadsHolderData: UploadsHolderData): UploadsHolder => {
    const {
        collectionRefElements, elementsValues, translate, uploadsConfig, index, translationPath,
    }: UploadsHolderData = uploadsHolderData;

    const uploadsHolder: UploadsHolder = {
        uploadsConfig,
        name: uploadsConfig.name || `${translate(`${translationPath}.otherFiles.text`) as string}_${index}`,
    };

    const minCount: Nullable<number> = calculateCountReference(
        uploadsConfig.minCountReference,
        collectionRefElements,
        elementsValues,
    );
    const maxCount: Nullable<number> = calculateCountReference(
        uploadsConfig.maxCountReference,
        collectionRefElements,
        elementsValues,
    );

    if (minCount) {
        uploadsHolder.minCount = minCount;
    }
    if (maxCount) {
        uploadsHolder.maxCount = maxCount;
    }

    return uploadsHolder;
};

/**
 * Calculates acceptable MimeType-s, and creates valid UploadsHolder-s for given schema config.
 *
 * @param {UploadsData} uploadsData
 * @return {UploadsData}
 */
export const initUploadsData = (uploadsData: InitUploadsData): UploadsData => {
    const {
        editor,
        translate,
        elementsValues,
        collectionRefElements,
        translationPath,
        tasks,
        elementsValuesMap,
        schemaKey,
        uploadBucket,
    }: InitUploadsData = uploadsData;
    let mimeTypes: string[] = ["*/*"];
    const uploadsHolders: UploadsHolder[] = [];
    let fileNamesList: string[] = [];
    if (editor) {
        const {
            batchUploads, acceptedMimeTypes, fileNameReference,
        }: EditorType = editor;
        fileNamesList = getUploadsFileNames({
            tasks,
            uploadBucket,
            fileNameReference,
            elementsValuesMap,
            schemaKey,
        });
        mimeTypes = [];
        if (batchUploads) {
            batchUploads.forEach((uploadsConfig: UploadsConfig, index: number) => {
                mimeTypes = mimeTypes.concat(uploadsConfig.mimeTypes);
                uploadsHolders.push(
                    initUploadsHolder({
                        uploadsConfig,
                        translate,
                        elementsValues,
                        collectionRefElements,
                        index,
                        translationPath,
                    }),
                );
            });
        } else if (acceptedMimeTypes) {
            mimeTypes = acceptedMimeTypes;
        }
    }
    return {
        uploadsHolders,
        acceptedMimeTypes: [...new Set(mimeTypes)],
        fileNamesList,
    };
};

export const removeFileFromBucket = (file: UploadFile, selectedFiles: SelectedFiles): SelectedFiles => {
    const uploadFiles: SelectedFiles = {...selectedFiles};
    const bucketKey: Nullable<string> = Object.keys(uploadFiles).find(
        (key: string): boolean => Object.values(uploadFiles[key]).some(
            (uploadFile: UploadFile) => uploadFile === file,
        ),
    );
    if (bucketKey) {
        const bucket: UploadsBucket = uploadFiles[bucketKey];
        const taskId: Nullable<string> = Object.keys(bucket).find(
            (key: string): boolean => bucket[key] === file,
        );
        if (taskId) {
            delete bucket[taskId];
        }
    }
    return uploadFiles;
};

export const isReadOnlyTask = (phases: TaskPhase[], phaseName: string): boolean => {
    const taskPhase: Nullable<TaskPhase> = phases.find((phase: TaskPhase) => phase.name === phaseName);
    const readyOnlyStatuses: Status[] = [
        Status.NOT_STARTED,
        Status.COMPLETED,
        Status.SKIPPED,
        Status.FAILED,
        Status.REJECTED,
    ];
    return (taskPhase ? readyOnlyStatuses.includes(taskPhase.status) : false);
};

export const isTaskChangeable = (status: Status): boolean => [
    Status.READY,
    Status.REOPENED,
    Status.IN_PROGRESS,
].includes(status);

/**
 * Task is finished if it is COMPLETED or REJECTED
 *
 * @param {Status} status
 */
export const isTaskFinished = (status: Status): boolean => [
    Status.COMPLETED,
    Status.REJECTED,
].includes(status);

/**
 * Task is rejected if it is REOPENED or REJECTED
 *
 * @param {Status} status
 */
export const isTaskRejected = (status: Status): boolean => [
    Status.REOPENED,
    Status.REJECTED,
].includes(status);

export const isEditableTask = (activeTask: Nullable<Task>, tasks: TaskEntity[]): boolean => {
    let isReopened: boolean = false;
    if (activeTask) {
        const taskEntity: Nullable<TaskEntity> = tasks.find((task: TaskEntity) => task.payload === activeTask.id);
        if (taskEntity) {
            isReopened = isTaskChangeable(taskEntity.status);
        }
    }
    return isReopened;
};

export const mapTasksToBucketKeys = (selectedFiles: SelectedFiles): TaskIdBucketKey => {
    const taskIdBucketKey: TaskIdBucketKey = {};
    Object.keys(selectedFiles).forEach((bucketKey: string) => {
        Object.keys(selectedFiles[bucketKey]).forEach((taskId: string) => {
            taskIdBucketKey[taskId] = bucketKey;
        });
    });
    return taskIdBucketKey;
};

export const batchUploadsAreValid = (batchUploads: UploadsConfig[], uploadReference: Nullable<string>): boolean => {
    let isValid: boolean = false;
    if (uploadReference) {
        const haveUploadProperty: boolean = !batchUploads.some((uploadConfig: UploadsConfig) => !uploadConfig.upload);
        if (haveUploadProperty) {
            const uploadIds: string[] = batchUploads.map((uploadConfig: UploadsConfig): string => uploadConfig.upload!);
            const uploadIdsSet: Set<string> = new Set(uploadIds);
            isValid = uploadIds.length === uploadIdsSet.size;
        }
    }

    return isValid;
};

export const getRejectionReason = (editor: Nullable<EditorType>, elementsValues: ElementsValues): Nullable<string> => {
    let rejectionReason: Nullable<string> = null;
    if (editor?.rejectionReasonReference && elementsValues[editor?.rejectionReasonReference]) {
        rejectionReason = elementsValues[editor?.rejectionReasonReference] as string;
    }
    return rejectionReason;
};

export const getStatusIconData = (statusIconInfo: StatusIconInfo): StatusIconData => {
    const {
        status, isActiveTask, activeTaskInvalid, translationPath, translate,
    }: StatusIconInfo = statusIconInfo;
    let statusClass: string;
    let statusName: string = translate(`${translationPath}.${Status[status]}.text`) as string;
    switch (status) {
        case Status.NOT_STARTED:
            statusClass = "df-not-started";
            break;
        case Status.READY:
        case Status.SKIPPED:
        case Status.FAILED:
        case Status.REOPENED:
        case Status.REJECTED:
        case Status.COMPLETED:
            statusClass = `df-${Status[status].toLowerCase()}`;
            break;
        case Status.IN_PROGRESS:
            if (isActiveTask && activeTaskInvalid) {
                statusClass = "df-invalid-draft";
                statusName = translate(`${translationPath}.inProgressInvalid.text`) as string;
            } else {
                statusClass = "df-in-progress";
            }
            break;

        default:
            statusClass = "";
            break;
    }
    return {statusClass, statusName};
};

export const getTaskPhaseStatus = (taskEntity: TaskEntity): Status => {
    const {phase}: HistoryParams = getHistoryParams();
    const {phases}: TaskEntity = taskEntity;
    const collection: Nullable<TaskPhase> = phases.find((taskPhase: TaskPhase): boolean => taskPhase.name === phase);
    return (collection ? collection.status : Status.NOT_STARTED);
};

/**
 * Returns true if Intaker version of the app is running.
 *
 * @param {DisplayType} displayType
 * @return {boolean}
 */
export const isIntaker = (displayType: DisplayType): boolean => [
    DisplayType.INTAKER_WIZARD,
    DisplayType.INTAKER_SCROLL,
].includes(displayType);

/**
 * Calculates number of valid session tasks.
 * Task is considered to be valid if it's metadata is valid,
 * and task upload file is selected.
 *
 * @param {CountValidTasksData} countValidTasksData
 * @return {number}
 */
export const countValidTasks = (countValidTasksData: CountValidTasksData): number => {
    const {formErrorsPayload, uploadBucket}: CountValidTasksData = countValidTasksData;
    const {tasks}: GetFormErrorsPayload = formErrorsPayload;

    let validTasksCount: number = 0;
    if (isJobMetadataValid(formErrorsPayload)) {
        const sessionTasks: TaskEntity[] = getSessionTasks(tasks);

        sessionTasks.forEach(({id: taskEntityId, payload: taskId}: TaskEntity) => {
            const errors: FieldValidationError[] = getTaskValidationErrors(formErrorsPayload, taskEntityId);
            if (!errors.length && !!uploadBucket[taskId]) {
                validTasksCount += 1;
            }
        });
    }

    return validTasksCount;
};

/**
 * Sets status of each task to its phase status.
 *
 * @param {TaskEntity[]} tasks
 * @return {TaskEntity[]}
 */
export const mapPhaseStatus = (tasks: TaskEntity[] = []): TaskEntity[] => tasks.map(
    (task: TaskEntity): TaskEntity => {
        let status: Status = Status.NOT_STARTED;
        const historyParams: Nullable<HistoryParams> = getHistoryParams();
        if (historyParams) {
            const {phase: phaseName}: HistoryParams = historyParams;
            const taskPhase: Nullable<TaskPhase> = task.phases.find(
                (phase: TaskPhase): boolean => phase.name === phaseName,
            );
            if (taskPhase) {
                status = taskPhase.status;
            }
        }

        return {
            ...task,
            status,
        };
    },
);

/**
 * Determines Upload Config key, based on schema config.
 *
 * @param {BucketKeyData} bucketKeyData
 * @return {string}
 */
export const determineBucketKey = (bucketKeyData: BucketKeyData): string => {
    const {
        useBucketConfig, uploadsHolder, validBatchUploadsConfig,
    }: BucketKeyData = bucketKeyData;
    let bucketKey: string = "Files";
    if (useBucketConfig) {
        if (validBatchUploadsConfig) {
            bucketKey = uploadsHolder.uploadsConfig.upload || "";
        } else {
            bucketKey = uploadsHolder.name;
        }
    }
    return bucketKey;
};

/**
 * Returns boolean true if number of files in bucket is valid.
 *
 * @param {BucketCountData} bucketCountData
 * @return {boolean}
 */
export const isValidNumberOfFiles = (bucketCountData: BucketCountData): boolean => {
    const {minCount, maxCount, uploaded}: BucketCountData = bucketCountData;
    const required: number = minCount || 0;
    let isValidFileCount: boolean = false;
    if (minCount && maxCount && minCount === maxCount) {
        isValidFileCount = uploaded === required;
    } else if (minCount && !maxCount) {
        isValidFileCount = uploaded >= required;
    } else if ((!minCount && maxCount) || (minCount && maxCount && minCount !== maxCount)) {
        isValidFileCount = uploaded >= required && uploaded <= maxCount;
    }
    return isValidFileCount;
};

/**
 * Calculates number of files, already selected for provided bucket
 *
 * @param {DetermineUploadedFilesData} data
 * @return {number}
 */
export const determineNumberOfUploadedFiles = (data: DetermineUploadedFilesData): number => {
    const {
        uploadsHolder,
        uploadFiles: {selectedFiles, finishedUploads: {bucketCount}},
        validBatchUploadsConfig,
    }: DetermineUploadedFilesData = data;
    const bucketKey: string = determineBucketKey({
        useBucketConfig: true,
        validBatchUploadsConfig,
        uploadsHolder,
    });
    const uploaded: number = bucketCount[bucketKey] ? bucketCount[bucketKey] : 0;
    const selected: number = selectedFiles && selectedFiles[bucketKey]
        ? Object.values(selectedFiles[bucketKey]).length
        : 0;
    return (uploaded + selected);
};

/**
 * Returns boolean true if selected files are valid by bucket configuration
 *
 * @param {ValidByBucketsData} data
 * @return {boolean}
 */
export const isValidByBuckets = (data: ValidByBucketsData): boolean => {
    const {
        uploadsData: {uploadsHolders},
        uploadFiles,
        validBatchUploadsConfig,
    }: ValidByBucketsData = data;

    return !uploadsHolders.some((uploadsHolder: UploadsHolder): boolean => {
        const {minCount = 0, maxCount}: UploadsHolder = uploadsHolder;
        let validByBucket: boolean = true;
        if (minCount || maxCount) {
            const uploaded: number = determineNumberOfUploadedFiles({
                uploadsHolder,
                uploadFiles,
                validBatchUploadsConfig,
            });
            validByBucket = isValidNumberOfFiles({minCount, maxCount, uploaded});
        }
        return !validByBucket;
    });
};

/**
 * Help method to prepare array data for params on get request
 * @param {any} params
 * @return {string}
 */
export const noEncodingSerializer = (params?: any): string => {
    let options: string = "";
    Object.keys(params).forEach((key: string) => {
        const obj: any = params[key];
        if (typeof obj !== "object" && obj !== undefined) {
            options += `${key}=${obj}&`;
        } else if (typeof obj === "object" && obj && obj.length) {
            let counter: number = 0;
            while (counter < obj.length) {
                options += `${key}=${params[key][counter]}&`;
                counter += 1;
            }
        }
    });
    return options ? options.slice(0, -1) : options;
};

/**
 * Pack refElements of a schema element (batch or task element references) and all its children in to single RefElementsMap.
 *
 * @param references {Nullable<RefElementsMap>}
 * @param allReferences {RefElementsMap}
 * @return {RefElementsMap}
 */
export const getElementReferences = (
    references: Nullable<RefElementsMap>,
    allReferences: RefElementsMap,
): RefElementsMap => {
    let elementReferences: RefElementsMap = {...allReferences};
    if (references) {
        elementReferences = {...elementReferences, ...references};

        Object.keys(references).forEach((elementRef: string): void => {
            const {refElements}: AnyElementType = references![elementRef];
            if (refElements) {
                elementReferences = getElementReferences(refElements, elementReferences);
            }
        });
    }
    return elementReferences;
};

/**
 * If there is a property in "current" JobMetadata, that does not exist on the "next" JobMetadata,
 * init property in next JobMetadata to schema default value.
 *
 * @param {SetJobMetadataDefaultsData} data
 * @return {ElementsValues}
 */
export const setJobMetadataDefaults = (data: SetJobMetadataDefaultsData): ElementsValues => {
    const {current, next, editor}: SetJobMetadataDefaultsData = data;
    const nextWithDefaults: ElementsValues = {...next};

    Object.keys(current).forEach((reference: string): void => {
        if (!Object.prototype.hasOwnProperty.call(next, reference)
            && editor.refElements
            && editor.batchFieldReferences) {
            let batchReferences: RefElementsMap = {};
            editor.batchFieldReferences.forEach((ref: string): void => {
                batchReferences[ref] = editor!.refElements![ref];
                const {refElements}: AnyElementType = editor!.refElements![ref];
                batchReferences = getElementReferences(refElements, batchReferences);
            });

            const element: Nullable<AnyElementType> = batchReferences[reference];

            if (element) {
                nextWithDefaults[reference] = getSchemaElementDefaultValue(element) as AnyElementValue;
            }
        }
    });

    return nextWithDefaults;
};

/**
 * If there is a property in "current" TaskMetadata, that does not exist on the "next" TaskMetadata,
 * init property in next TaskMetadata to schema default value.
 *
 * @param {SetTaskMetadataDefaultsData} data
 * @return {ElementsValues}
 */
export const setTaskMetadataDefaults = (data: SetTaskMetadataDefaultsData): ElementsValues => {
    const {current, next, editor}: SetTaskMetadataDefaultsData = data;
    const nextWithDefaults: ElementsValues = {...next};

    Object.keys(current).forEach((reference: string): void => {
        if (!Object.prototype.hasOwnProperty.call(next, reference)
            && editor.refElements
            && editor.spanReferences) {
            let elementReferences: RefElementsMap = {};

            const spanElements: AnyElementType[] = [];
            editor.spanReferences.forEach((spanReference: string) => {
                spanElements.push(editor!.refElements![spanReference]);
            });

            spanElements.forEach((spanElement: AnyElementType) => {
                const {refElements}: AnyElementType = spanElement;
                elementReferences = getElementReferences(refElements, elementReferences);
            });

            const element: Nullable<AnyElementType> = elementReferences[reference];

            if (element) {
                nextWithDefaults[reference] = getSchemaElementDefaultValue(element) as AnyElementValue;
            }
        }
    });

    return nextWithDefaults;
};

/**
 * If applicable, init next task/job Metadata props to schema default values.
 *
 * @param {GetUpdatedElementMetadataInfo} data
 * @return {Nullable<ElementsValues>}
 */
export const getUpdatedElementMetadata = (data: GetUpdatedElementMetadataInfo): Nullable<ElementsValues> => {
    const {
        current, next, editor, elementsValuesType,
    }: GetUpdatedElementMetadataInfo = data;
    let elementMetadata: Nullable<ElementsValues> = next;

    if (current && next) {
        if (elementsValuesType === ElementsValuesType.TASK) {
            elementMetadata = setTaskMetadataDefaults({current, next, editor});
        } else {
            elementMetadata = setJobMetadataDefaults({current, next, editor});
        }
    }
    return elementMetadata;
};

/**
 * Returns a list of all comparator values from element comparison items.
 *
 * @param {Nullable<ComparisonItems>} comparatorItems
 * @param {ComparisonReferences} reference
 * @return {Nullable<AnyElementValue>[]}
 */
export const getComparatorValues = (
    comparatorItems: Nullable<ComparisonItems>,
    reference: ComparisonReferences,
): Nullable<AnyElementValue>[] => {
    let values: Nullable<AnyElementValue>[] = [];

    if (comparatorItems && comparatorItems[reference]) {
        values = comparatorItems[reference]!.map(({value}: ComparisonItem): Nullable<AnyElementValue> => value);
    }

    return values;
};

/**
 * Checks if the phase has expired
 *
 * @param {PhaseAccessStatus} [status]
 * @return {boolean}
 */
export const isPhaseExpired = (status?: PhaseAccessStatus): boolean => status === PhaseAccessStatus.EXPIRED;

/**
 * Checks if the job has expired
 *
 * @param {Status} phaseStatus
 * @param {PhaseAccessStatus | undefined} accessStatus
 * @return {boolean}
 */
export const isJobExpired = ({accessStatus, phaseStatus}: JobExpiredInfo): boolean => {
    const {COMPLETED, REJECTED}: typeof Status = Status;
    return (isPhaseExpired(accessStatus) && ![COMPLETED, REJECTED].includes(phaseStatus));
};
